<script setup>
import { openModal } from '@kolirt/vue-modal';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useCustomerStore } from '@/stores/customer';
import Login from '@/components/modals/Login.vue';
import Register from '@/components/modals/Register.vue';
import VerificationEmail from '@/components/modals/VerificationEmail.vue';
import LiveTableOverlayModalSlotegrator from '@/components/live_casino/table/LiveTableOverlayModalSlotegrator.vue';
import DealerComponent from '../DealerComponent.vue';
import { useCurrenciesStore } from '@/stores/currencies';
import { useGeneralStore } from '@/stores/general';

const router = useRouter();
const customerStore = useCustomerStore();
const currenciesStore = useCurrenciesStore();
const generalStore = useGeneralStore();

// Props
const props = defineProps({
	tables: {
		type: Object,
		required: true
	},
	size: {
		type: String,
		default: 'small'
	},
	force_opacity: {
		type: Boolean,
		default: false
	},
});

// On mobile show modal instead of overlay
const showOverlayModal = (lobby, table, image_url) => {
	openModal(LiveTableOverlayModalSlotegrator, { lobby: lobby, table: table, image_url: image_url }).catch(() => { });
};

// Show Register Modal
const showRegisterModal = () => {
	openModal(Register).then((data) => {
		if (data.show_login) showLoginModal();
	}).catch(() => { });
};

// Show Login Modal
const showLoginModal = () => {
	openModal(Login).then((data) => {
		if (data.show_register) showRegisterModal();
	}).catch(() => { });
};

// Show Verification Mail Modal
const showVerificationEmailModal = () => {
	openModal(VerificationEmail).catch(() => { });
};

const has_error = ref(false);
const setError = () => {
	has_error.value = true;
};

const hasError = computed(() => {
	return has_error.value;
});

const imageUrl = computed(() => {
	let url = props.tables.image_url;

	if (url != null && url != '' && generalStore.isLicenseEnabled == 'true' && !url.startsWith('http')) url = process.env.VUE_APP_URL + url;

	// const url_pattern = /^(https?:\/\/)?([\w.]+)\.([a-z]{2,})(\/\S*)?$/;
	const url_pattern = /^(https?:\/\/)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,6})(\/[\S]*)?$/
	let pattern_test = url_pattern.test(url);

	if (!url || url == '' || url == null || url == undefined || !pattern_test || !isImageUrlValid.value) {
		return '/assets/images/brand_logo.svg';
	}

	return url;
});

const isDefaultImage = computed(() => {
	if (imageUrl.value == '/assets/images/brand_logo.svg' || has_error.value == true) {
		return true;
	}

	return false;
});


const isImageUrlValid = computed(() => {
	let url = props.tables.image_url;
	let url_valid = true;

	// Check if the URL is empty or null
	if (!url || url == '') {
		url_valid = false;
	} else {
		// Try to create a new URL object to verify URL format
		try {
			new URL(url);
		} catch (e) {
			url_valid = false; // URL is not valid
		}

		// Extract the filename and validate it
		if (url_valid) {
			const fileName = url.split('/').pop();
			if (!fileName || fileName.includes('..')) {
				url_valid = false; // Filename is not valid
			}

			// Regular expression to check for valid image file extensions
			const validImageRegex = /\.(jpeg|jpg|png)$/i;
			if (!validImageRegex.test(fileName)) {
				url_valid = false; // File extension is not valid
			}
		}
	}

	if (!url_valid) {
		return false;
	}

	return true;
});

const visibilityChanged = (is_visible, entry) => {
	if (is_visible) {
		const img_element = entry.target;
		const data_src = img_element.getAttribute('data-src');
		if (data_src) {
			img_element.setAttribute('src', data_src);
			img_element.removeAttribute('data-src');

			if (data_src != '/assets/images/brand_logo.svg') {
				img_element.classList.remove('default-image');

				// Get the parent element of the image
				const parentElement = img_element.parentNode;

				// Remove 'default-image-container' class from the parent
				if (parentElement.classList.contains('default-image-container')) {
					parentElement.classList.remove('default-image-container');
				}
			}
		}
	}
};

// Go To Table
const goToTable = (table_id, param_id) => {
	let router_object = { name: 'live-casino-table', params: { table_id: table_id, param_id: param_id } };

	// Check if Customer is logged in
	if (!customerStore.isLoggedIn) {
		useCustomerStore().redirect_after_login = router_object;

		showLoginModal();

		return;
	}

	// Check if Customer has verified email
	if (!customerStore.hasVerifiedEmail) {
		showVerificationEmailModal();

		return;
	}

	// Go to Table's view
	router.push(router_object);
}

const selectPlayButtonClass = computed(() => {
	if (props.size == 'extra-small') {
		return 'play-button-extra-small';
	} else if (props.size == 'small') {
		return 'play-button-small';
	} else if (props.size == 'medium') {
		return 'play-button-medium';
	} else if (props.size == 'large') {
		return 'play-button-large';
	}

	return 'play-button-medium';
});

const openOverlay = (lobby, table, image_ur) => {
	if (isMobileDevice()) {
		showOverlayModal(lobby, table, image_ur);
	}
};

function isMobileDevice() {
	if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
		return true;
	}

	return false;
}
</script>
<template>
	<div v-for="live_table in props.tables['lobby']" :key="live_table.id" class="featured-game-inner">
		<div class="feature-image" :class="live_table['isOpen'] == 1 ? 'online-indicator' : 'offline-indicator'">
			<div @click="openOverlay(props.tables, live_table, imageUrl)" class="default-image-container"
				:class="['image-container', props.force_opacity == true ? 'default-image-container-dark' : '']">
				<img v-if="!hasError" src="/assets/images/brand_logo.svg" :data-src="imageUrl"
					:class="{ 'game-image-hover': true, 'default-image': isDefaultImage }" :alt="props.tables.name"
					@error="setError()" v-observe-visibility="visibilityChanged">

				<img v-else src="/assets/images/brand_logo.svg" data-src="/assets/images/brand_logo.svg"
					:alt="props.tables.name" class="default-image game-image-hover"
					v-observe-visibility="visibilityChanged">


				<div v-if="live_table['isOpen'] == 0" class="table-schedule">
					<span class="table-schedule-label">{{ $t('general.schedule') }}</span>
					<span class="table-schedule-time">{{ live_table['openTime'] }} - {{ live_table['closeTime'] }}</span>
				</div>

				<div v-if="live_table['limits'].length > 0" class="game-limits">Min {{ currenciesStore.format_currency(live_table['limits'][0]['min'], live_table['limits'][0]['currency']) }} Max {{ currenciesStore.format_currency(live_table['limits'][0]['max'], live_table['limits'][0]['currency']) }}</div>

				<DealerComponent :image_url="live_table['dealerAvatar']" :dealer_name="live_table['dealerName']"/>

				<div v-if="!isMobileDevice()" class="overlay" :class="isDefaultImage ? 'overlay-light' : ''">
					{{ props.tables.favourite }}
					<button class="play-button-container">
						<div class='play-button' :class="selectPlayButtonClass"
							@click="goToTable(props.tables.id, live_table.tableId)">
							<svg version="1.1" xmlns="http://www.w3.org/2000/svg"
								xmlns:xlink="http://www.w3.org/1999/xlink"
								xmlns:a="http://ns.adobe.com/AdobeSVGViewerExtensions/3.0/" x="0px" y="0px"
								width="213.7px" height="213.7px" viewBox="0 0 213.7 213.7"
								enable-background="new 0 0 213.7 213.7" xml:space="preserve">
								<polygon class='triangle' id="XMLID_18_" fill="none" stroke-width="7"
									stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10"
									points="73.5,62.5 148.5,105.8 73.5,149.1 " />
								<circle class='circle' id="XMLID_17_" fill="none" stroke-width="7"
									stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" cx="106.8"
									cy="106.8" r="103.3" />
							</svg>
						</div>
					</button>
				</div>
			</div>
		</div>
		<div class="feature-title">
			<h4 class="ellipsis" :alt="live_table.name">{{ live_table.name }}</h4>
		</div>
	</div>
</template>

<style scoped>
.default-image-container {
	background-color: rgb(0 0 0 / 20%);
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
}

.default-image-container-dark {
	background-color: rgb(0 0 0 / 80%);
}

.default-image {
	width: auto !important;
	height: 85% !important;
	max-height: 185px;
	opacity: 0.3;
}

.game-image-hover {
	height: 100%;
	transition: transform 0.3s ease;
}

.image-container:hover .game-image-hover {
	transform: scale(1.1);
}

.image-container {
	position: relative;
	width: 100%;
	height: 100%;
}

.image-container .overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.8);
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.3s, visibility 0.3s;
}

.image-container .overlay-light {
	background-color: rgba(0, 0, 0, 0.5);
}

.image-container:hover .overlay {
	opacity: 1;
	visibility: visible;
}

.play-button-container {
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
}

.demo-text {
	margin-top: 8px;
	color: white;
	font-size: 13px;
	transition: transform 0.3s ease-in-out;
}

.demo-text-small {
	margin-top: 7px;
	font-size: 12px;
}

.demo-text-large {
	margin-top: 16px;
	font-size: 15px;
}

.demo-text:hover {
	color: rgb(204, 204, 204);
}
</style>

<style lang="scss" scoped>
.circle {
	stroke: #a5a5a5;
	stroke-dasharray: 650;
	stroke-dashoffset: 0;
	-webkit-transition: all 0.5s ease-in-out;
	opacity: 1;
}

.play-button svg {
	height: 40px;
	width: 40px;
}

.play-button {
	height: 40px;
	width: 40px;
	display: inline-block;
	-webkit-transition: all 0.5s ease;
	margin-bottom: 10px;

	.triangle {
		-webkit-transition: all 0.7s ease-in-out;
		stroke-dasharray: 240;
		stroke-dashoffset: 0;
		stroke: #a5a5a5;
		transform: translateY(0) scale(1);
	}

	&:hover {

		.triangle {
			stroke-dashoffset: 480;
			opacity: 1;
			stroke: #a5a5a5;
			animation: nudge 0.7s ease-in-out;
			transform: translateY(0) scale(1.3);
			transform-origin: center;

			@keyframes nudge {
				0% {
					transform: translateX(0) scale(1);
				}

				30% {
					transform: translateX(-5px) scale(1);
				}

				50% {
					transform: translateX(5px);
				}

				70% {
					transform: translateX(-2px) scale(1);
				}

				100% {
					transform: translateX(0) scale(1.3);
				}
			}
		}

		.circle {
			stroke-dashoffset: 650;
			opacity: 0.3;
		}

	}
}

.play-button-extra-small {
	height: 30px;
	width: 30px;
}

.play-button-extra-small svg {
	height: 30px;
	width: 30px;
}

.play-button-small {
	height: 35px;
	width: 35px;
}

.play-button-small svg {
	height: 35px;
	width: 35px;
}

.play-button-medium svg {
	height: 45px;
	width: 45px;
}

.play-button-medium {
	height: 45px;
	width: 45px;
}

.play-button-large svg {
	height: 60px;
	width: 60px;
}

.play-button-large {
	height: 60px;
	width: 60px;
}

.favourite-container {
	position: absolute;

	background-color: rgba(0, 0, 0, 0.5);
	padding: 5px;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	transition: background-color 0.3s ease;
}

.favourite-container-extra-small {
	top: 0px;
	right: 0px;
}

.favourite-container .fav-svg-extra-small {
	width: 12px;
	height: 12px;
}

.favourite-container-small {
	top: 8px;
	right: 8px;
}

.favourite-container .fav-svg-small {
	width: 14px;
	height: 14px;
}


.favourite-container-large {
	top: 15px;
	right: 15px;
}

.favourite-container .fav-svg-large {
	width: 18px;
	height: 18px;
}

.favourite-container:hover svg {
	opacity: 0.8;
}

.casino-game-section.live_casino-game-section .casino-feature-games .casino-feature-outer .feature-games-main .featured-game-inner .feature-image img {
	-o-object-fit: inherit;
	object-fit: inherit;
}

.online-indicator::after {
	content: '';
	width: 12px;
	height: 12px;
	background-color: #00FF00;
	border-radius: 50%;
	position: absolute;
	top: 8px;
	right: 8px;
	border: 2px solid #131C27;
}

.offline-indicator::after {
	content: '';
	width: 12px;
	height: 12px;
	background-color: red;
	border-radius: 50%;
	position: absolute;
	top: 8px;
	right: 8px;
	border: 2px solid #131C27;
}

.game-limits {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: rgba(0, 0, 0, 0.5);
	color: white;
	padding: 5px 10px;
	border-radius: 0 0 5px 5px;
	font-size: 12px;
	text-align: center;
	white-space: nowrap;
	z-index: 9999;
}

.table-schedule {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: rgba(0, 0, 0, 0.8);
	color: white;
	padding: 5px 10px;
	border-radius: 0 0 5px 5px;
	font-size: 12px;
	text-align: center;
	white-space: nowrap;
}

.table-schedule-label {
	display: block;
	margin-top: 20%;
}

.table-schedule-time {
	display: block;
}

.feature-title {
	overflow: hidden;
}

.featured-game-inner {
	width: 100%;
}

.feature-title {
	width: 100%;
}

/* .feature-title h4 {
	min-width: -webkit-fill-available !important;
} */

@media screen and (min-width: 320px) and (max-width: 320px) {
	.feature-title h4 {
		width: 125px !important;
	}
}

@media screen and (min-width: 375px) and (max-width: 500px) {
	.feature-title h4 {
		width: 132px !important;
	}
}

@media screen and (min-width: 501px) and (max-width: 630px) {
	.feature-title h4 {
		width: 180px !important;
	}
}

@media screen and (min-width: 631px) and (max-width: 767px) {
	.feature-title h4 {
		width: 125px !important;
	}
}

@media screen and (min-width: 772px) and (max-width: 1100px) {
	.feature-title h4 {
		width: 255px !important;
	}
}

@media screen and (min-width: 1100px) and (max-width: 1190px) {
	.feature-title h4 {
		width: 215px !important;
	}
}

@media screen and (min-width: 1190px) and (max-width: 1499px) {
	.feature-title h4 {
		width: 215px !important;
	}
}

@media screen and (min-width: 1500px) and (max-width: 1600px) {
	.feature-title h4 {
		width: 185px !important;
	}
}

@media screen and (min-width: 1600px) and (max-width: 1700px) {
	.feature-title h4 {
		width: 254px !important;
	}
}

@media screen and (min-width: 1701px) {
	.feature-title h4 {
		width: 254px !important;
	}
}

.casino-game-section.live_casino-game-section .casino-feature-games .casino-feature-outer .feature-games-main .featured-game-inner .feature-image img {
	-o-object-fit: inherit;
	object-fit: inherit;
}

.casino-game-section.live_casino-game-section .casino-feature-games .casino-feature-outer {
	border-bottom: unset;
}

.view-all-btn-full-width:before {
	width: 100% !important;
}

.ellipsis {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
	max-width: 270px;
}
</style>